import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CreditCard, Loader2, CheckCircle2, Building2, AlertCircle, Landmark, Wallet } from "lucide-react";
import { axiosInstance } from "../lib/axios";

interface Method { key: string; label: string; publishableKey?: string }
interface PayInvoice {
  invoiceNumber: string; invoiceType: string;
  client: { name: string; company: string } | null;
  project: string; milestoneTitle: string; description: string;
  amount: number; currency: string; dueDate: string | null;
  paymentStatus: string; paidAmount: number;
  environment: string; methods: Method[]; company: string;
  bankDetails?: {
    accountName?: string; bankName?: string; accountNumber?: string;
    iban?: string; swift?: string; routingNumber?: string; instructions?: string;
  } | null;
}

const money = (n: number, c: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: c || "USD" }).format(n || 0);
const dateStr = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—");

export default function PaymentPage() {
  const { token = "" } = useParams();
  const [sp] = useSearchParams();
  const [inv, setInv] = useState<PayInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>("");        // which method is processing
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  const load = async () => {
    try {
      const { data } = await axiosInstance.get(`/pay/${token}`);
      setInv(data);
      if (data.paymentStatus === "Paid") setDone(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || "This invoice link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  // On return from a gateway, confirm/capture the payment.
  useEffect(() => {
    const status = sp.get("status");
    const provider = sp.get("provider");
    (async () => {
      if (status === "success" && provider === "stripe") {
        setConfirming(true);
        try {
          const { data } = await axiosInstance.post(`/pay/${token}/stripe/confirm`, { sessionId: sp.get("session_id") });
          if (data.paid) setDone(true);
        } catch { /* fall through to load */ } finally { setConfirming(false); }
      } else if (status === "success" && provider === "paypal") {
        setConfirming(true);
        try {
          const { data } = await axiosInstance.post(`/pay/${token}/paypal/capture`, { orderId: sp.get("token") });
          if (data.paid) setDone(true);
        } catch { /* fall through */ } finally { setConfirming(false); }
      }
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const payStripe = async () => {
    setBusy("stripe"); setError("");
    try {
      const { data } = await axiosInstance.post(`/pay/${token}/stripe`);
      if (data.url) window.location.href = data.url;
    } catch (e: any) { setError(e?.response?.data?.message || "Couldn't start the payment."); setBusy(""); }
  };
  const payPaypal = async () => {
    setBusy("paypal"); setError("");
    try {
      const { data } = await axiosInstance.post(`/pay/${token}/paypal`);
      if (data.approveUrl) window.location.href = data.approveUrl;
    } catch (e: any) { setError(e?.response?.data?.message || "Couldn't start the payment."); setBusy(""); }
  };

  const cancelled = sp.get("status") === "cancelled";

  const iconFor = (k: string) => k === "stripe" ? <CreditCard size={18} /> : k === "paypal" ? <Wallet size={18} /> : k === "bank_transfer" ? <Landmark size={18} /> : <Building2 size={18} />;

  const content = useMemo(() => {
    if (loading || confirming) {
      return (
        <div className="py-20 grid place-items-center text-slate-500">
          <Loader2 className="animate-spin text-[#0058c3]" size={30} />
          <p className="mt-3 text-sm">{confirming ? "Confirming your payment…" : "Loading invoice…"}</p>
        </div>
      );
    }
    if (error && !inv) {
      return (
        <div className="py-16 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-rose-400 mb-3" />
          <p className="font-semibold text-slate-800">Invoice unavailable</p>
          <p className="text-sm text-slate-500 mt-1">{error}</p>
        </div>
      );
    }
    if (!inv) return null;

    return (
      <>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-[#0058c3] font-bold text-lg"><Building2 size={20} /> {inv.company}</div>
            <p className="text-xs text-slate-400 mt-0.5 font-mono">Invoice {inv.invoiceNumber}</p>
          </div>
          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {done ? "PAID" : inv.paymentStatus === "Pending" ? "PROCESSING" : "UNPAID"}
          </span>
        </div>

        {/* Details */}
        <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 mb-6">
          {inv.client && <Row label="Billed to" value={`${inv.client.name}${inv.client.company ? ` · ${inv.client.company}` : ""}`} />}
          {inv.project && <Row label="Project" value={inv.project} />}
          {inv.milestoneTitle && <Row label="Milestone" value={inv.milestoneTitle} />}
          {inv.description && <Row label="Description" value={inv.description} />}
          <Row label="Due date" value={dateStr(inv.dueDate)} />
        </div>

        {/* Amount */}
        <div className="rounded-xl text-white p-6 mb-6 text-center" style={{ background: "linear-gradient(135deg, var(--brand-hex, #0058c3), var(--brand-hex-dark, #003e8f))" }}>
          <p className="text-[11px] uppercase tracking-widest text-white/70">Amount due</p>
          <p className="text-4xl font-extrabold mt-1">{money(inv.amount, inv.currency)}</p>
          <p className="text-[11px] text-white/60 mt-1">{inv.currency} · {inv.environment === "live" ? "Secure payment" : "Sandbox / test payment"}</p>
        </div>

        {done ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-2" />
            <p className="font-bold text-slate-800 text-lg">Payment received</p>
            <p className="text-sm text-slate-500">Thank you — {money(inv.paidAmount || inv.amount, inv.currency)} paid for invoice {inv.invoiceNumber}.</p>
          </div>
        ) : (
          <>
            {cancelled && <p className="text-center text-sm text-amber-600 mb-3">Payment was cancelled — you can try again below.</p>}
            {error && <p className="text-center text-sm text-rose-600 mb-3">{error}</p>}
            <p className="text-xs font-semibold text-slate-500 mb-2">Choose a payment method</p>
            <div className="space-y-2">
              {inv.methods.length === 0 && <p className="text-sm text-slate-400">No payment method is available for this invoice. Please contact us.</p>}
              {inv.methods.filter((m) => m.key === "stripe" || m.key === "paypal").map((m) => (
                <button
                  key={m.key}
                  disabled={!!busy}
                  onClick={() => (m.key === "stripe" ? payStripe() : payPaypal())}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-[#0058c3] hover:bg-[#0058c3]/5 transition-all disabled:opacity-60"
                >
                  <span className="flex items-center gap-3 font-semibold text-slate-800">
                    <span className="w-9 h-9 rounded-lg bg-[#0058c3]/10 text-[#0058c3] grid place-items-center">{iconFor(m.key)}</span>
                    Pay with {m.label}
                  </span>
                  {busy === m.key ? <Loader2 className="animate-spin text-[#0058c3]" size={18} /> : <span className="text-[#0058c3] text-sm font-bold">Pay →</span>}
                </button>
              ))}

              {/* Bank Transfer — show details */}
              {inv.methods.some((m) => m.key === "bank_transfer") && inv.bankDetails && (
                <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                  <p className="flex items-center gap-2 font-semibold text-slate-800 mb-2"><Landmark size={16} className="text-[#0058c3]" /> Bank Transfer</p>
                  <dl className="text-sm text-slate-700 space-y-1">
                    {inv.bankDetails.accountName && <BankRow k="Account name" v={inv.bankDetails.accountName} />}
                    {inv.bankDetails.bankName && <BankRow k="Bank" v={inv.bankDetails.bankName} />}
                    {inv.bankDetails.accountNumber && <BankRow k="Account #" v={inv.bankDetails.accountNumber} />}
                    {inv.bankDetails.iban && <BankRow k="IBAN" v={inv.bankDetails.iban} />}
                    {inv.bankDetails.swift && <BankRow k="SWIFT/BIC" v={inv.bankDetails.swift} />}
                    {inv.bankDetails.routingNumber && <BankRow k="Routing #" v={inv.bankDetails.routingNumber} />}
                  </dl>
                  <p className="text-[12px] text-slate-500 mt-2">{inv.bankDetails.instructions || `Please use invoice ${inv.invoiceNumber} as the payment reference. We'll mark this invoice paid once the transfer is received.`}</p>
                </div>
              )}

              {/* Manual — offline arrangement */}
              {inv.methods.some((m) => m.key === "manual") && (
                <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
                  <p className="flex items-center gap-2 font-semibold text-slate-800 mb-1"><Building2 size={16} className="text-[#0058c3]" /> Manual Payment</p>
                  <p className="text-[13px] text-slate-500">This invoice is settled offline (e.g. cash, cheque, or another agreed method). Our team will record the payment and mark the invoice paid once received.</p>
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-400 text-center mt-5">Payments are processed securely by the selected provider. We never store your card details.</p>
          </>
        )}
      </>
    );
  }, [loading, confirming, error, inv, done, busy, cancelled]);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-6 sm:p-8">
        {content}
      </div>
    </div>
  );
}

function BankRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-slate-400">{k}</dt>
      <dd className="font-medium text-slate-800 font-mono text-[13px]">{v}</dd>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <span className="text-xs text-slate-400 uppercase tracking-wide font-semibold">{label}</span>
      <span className="text-sm text-slate-800 text-right font-medium">{value}</span>
    </div>
  );
}
